import {
  MEDIA_PRO_REQUIRED_REASON,
  MODERATION_BLOCKED_REASON,
  MODERATION_UNAVAILABLE_REASON,
} from '@astrally/shared';
import type {
  AllyTurnRequest,
  AllyTurnResponse,
  AppConfigValues,
  AstroSummary,
  CompatibilityDoc,
  ConnectionDoc,
  ConnectionIntent,
  ConversationDoc,
  CreditLedgerEntry,
  DiscoveryFeedResult,
  EntitlementDoc,
  MediaKind,
  MediaRef,
  MemoryItem,
  MessageDoc,
  MessageModerationStatus,
  RecommendationDoc,
  ReportReason,
  UserDoc,
} from '@astrally/shared';

/** Server-derived entitlement snapshot the UI renders from. */
export interface EntitlementSnapshot {
  entitlement: EntitlementDoc;
  tier: 'none' | 'base' | 'pro';
  quota: { remaining: number; kind: 'base-lifetime' | 'pro-daily' };
  creditBalance: number;
  config: {
    base: AppConfigValues['base'];
    /**
     * `discoveryDailyCards` and `fairUse` are optional because a deployed
     * `entitlementCheck` may predate them — the Pro comparison table falls
     * back to the shared config defaults for those two, never to a literal.
     */
    pro: Pick<AppConfigValues['pro'], 'products' | 'priceDisplay' | 'dailyOutgoingRequests'> &
      Partial<Pick<AppConfigValues['pro'], 'discoveryDailyCards' | 'fairUse'>>;
    creditCosts: AppConfigValues['creditCosts'];
    creditPacks: AppConfigValues['creditPacks'];
  };
}

/** Why a send was refused, when it was refused by moderation rather than by a fault. */
export type ModerationRefusal = 'blocked' | 'unavailable';

/**
 * Read a moderation refusal off a rejected `sendMessage`.
 *
 * The server puts a stable machine-readable `reason` in the callable error's
 * `details`; the two constants are shared so this never depends on the
 * wording, which is user-facing copy and translated. Returns null for
 * ordinary failures (offline, signed out, conversation ended) — those keep
 * showing the plain error text.
 */
export function moderationRefusal(error: unknown): ModerationRefusal | null {
  const reason = (error as { details?: { reason?: unknown } } | null)?.details?.reason;
  if (reason === MODERATION_BLOCKED_REASON) return 'blocked';
  if (reason === MODERATION_UNAVAILABLE_REASON) return 'unavailable';
  return null;
}

/**
 * True when a call was refused because the account is not Pro.
 *
 * Keyed off the shared constant in the callable error's `details`, never off
 * the message text — the wording is user-facing copy and is translated. Both
 * `feedPost` and `mediaGrantUpload` send it.
 */
export function mediaProRequired(error: unknown): boolean {
  return (error as { details?: { reason?: unknown } } | null)?.details?.reason ===
    MEDIA_PRO_REQUIRED_REASON;
}

/**
 * What the server knows about a message the moment after it is written.
 *
 * `media` exists only when the message carried an attachment, and it is the
 * answer to the question the sender cannot ask for themselves: their client
 * may not read the other member's entitlement document, so without this a Pro
 * user photographing something for a free user gets a successful send, a
 * normal-looking bubble, and no idea that the photo landed as a locked blur.
 * `MessageDoc.mediaLockedFor` is the durable copy of the same fact, for when
 * they scroll back tomorrow.
 */
export interface SentMessage {
  id: string;
  sentAtISO: string;
  moderation?: MessageModerationStatus;
  media?: {
    /** FALSE means the recipient saw a blurred placeholder, not the photo. */
    recipientCanView: boolean;
    /** False for every video — nothing checks video — and for an unchecked image. */
    checked: boolean;
  };
}

/* --- the members-only feed --------------------------------------------------

   THESE TYPES ARE DECLARED TWICE. `functions/src/feed/feedFns.ts` has a
   structurally identical `FeedPostView` / `FeedPage`, because the shared
   package was outside this change's file scope and the client and the callable
   cannot import each other. It is the wire contract, so a drift is a runtime
   bug that nothing here would catch: when either file is next opened, move
   both into packages/shared/src/types/feed.ts and delete the duplicate.
   `MediaRef` and `MessageModerationStatus` are imported from the shared
   package precisely so the parts that already had a home stay in it. */

/** One post as it arrives from `feedList` / `feedPost`. */
export interface FeedPost {
  id: string;
  authorUid: string;
  /** Resolved server-side at read time, so a rename is reflected immediately. */
  authorDisplayName: string;
  /** Free-form, at most FEED_TEXT_MAX characters. */
  text: string;
  createdAtISO: string;
  /** `${createdAtISO}#${id}` — the field the feed is ordered and paginated on. */
  sortKey: string;
  moderation: MessageModerationStatus;
  /**
   * Present only on Pro-authored posts. Safe for every viewer to hold: it
   * carries dimensions, a byte count and an uninvertible blur, never the
   * bytes and never a download URL. The bytes themselves are behind
   * storage.rules — see LockedMedia.
   */
  media?: MediaRef;
  /** What moderation knows about the FILE. Always 'unchecked' for video. */
  mediaModeration?: MessageModerationStatus;
  /** True when the signed-in account wrote it. */
  mine: boolean;
  /**
   * ENGAGEMENT. Optional because posts written before likes and comments
   * shipped carry neither counter and there is no backfill — read them as
   * `?? 0` at every use, never bare.
   */
  likeCount?: number;
  commentCount?: number;
  /**
   * Whether THIS account has liked it — the heart's filled state. Resolved
   * per-caller server-side; absent on an older cached page, so treat absent
   * as "not liked".
   */
  likedByMe?: boolean;
}

export interface FeedPage {
  posts: FeedPost[];
  /** Hand back to `listFeed` for the next page. Null means the end. */
  cursor: string | null;
}

/**
 * One comment as it arrives from `listComments` / `addComment`.
 *
 * Mirrors `FeedCommentDoc` in packages/shared plus the two per-caller flags
 * the server resolves. Unlike a post, `authorName` is frozen at write time —
 * see the note in packages/shared/src/types/feed.ts for why that asymmetry is
 * deliberate rather than an oversight.
 */
export interface FeedComment {
  id: string;
  uid: string;
  authorName: string;
  text: string;
  createdAtISO: string;
  moderation: MessageModerationStatus;
  /** True when the signed-in account wrote it. */
  mine: boolean;
  /** True for your own comment, or ANY comment on your own post. */
  canDelete: boolean;
}

export interface FeedCommentsPage {
  comments: FeedComment[];
  /** Hand back to `listComments` for the next page. Null means the end. */
  cursor: string | null;
}

/** What `toggleLike` reports back — the server's count, not the optimistic one. */
export interface FeedLikeResult {
  liked: boolean;
  likeCount: number;
}

/**
 * What `addComment` reports back: the id the server assigned, and whether a
 * moderation provider actually inspected the text.
 *
 * `moderation: 'unchecked'` is not a failure and must not be shown as one — it
 * means no provider looked (no key bound, or the vendor was down and the
 * fail-open policy applied). It is recorded honestly rather than upgraded to
 * 'clean'; see the OUTAGE POLICY in functions/src/safety/moderation.ts.
 */
export interface FeedCommentReceipt {
  commentId: string;
  moderation: MessageModerationStatus;
}

/**
 * A resized, encoded, blur-hashed file ready to upload — the output of
 * `preparePostImage` / `probeVideo` in app/src/lib/media.ts plus the blob.
 */
export interface PreparedUpload {
  kind: MediaKind;
  blob: Blob;
  contentType: string;
  width: number;
  height: number;
  blurhash: string;
  bytes: number;
  /** Videos only. */
  durationMs?: number;
}

export interface CompatibilityView {
  full: boolean;
  compatibility: Partial<CompatibilityDoc> &
    Pick<CompatibilityDoc, 'pairId' | 'overall' | 'scoreVersion'>;
  upgradeHint?: string;
}

/**
 * Backend abstraction. Two implementations:
 * - DemoBackend: fully local (IndexedDB + shared mock Ally + the real
 *   matching/compatibility engines over seeded people). Zero credentials.
 * - FirebaseBackend: Auth + Firestore + callable Functions.
 *
 * UI code depends only on this interface, keeping the cloud replaceable.
 */
export interface Backend {
  readonly kind: 'demo' | 'firebase';

  init(): Promise<void>;
  currentUid(): string | null;
  signIn(email: string, password: string): Promise<string>;
  signUp(email: string, password: string): Promise<string>;
  signOut(): Promise<void>;

  getUser(uid: string): Promise<UserDoc | null>;
  saveUser(user: UserDoc): Promise<void>;
  /** Sync ONLY derived chart features (never exact birth data) for matching. */
  saveAstroSummary(summary: AstroSummary): Promise<void>;
  /**
   * Cross-device curriculum progress (finished lesson ids). Optional because
   * demo mode is device-local by definition; callers must treat absence and a
   * signed-out null identically — the local copy always works.
   */
  getLearnProgress?(): Promise<string[] | null>;
  saveLearnProgress?(doneLessonIds: string[]): Promise<void>;
  pauseRecommendations(paused: boolean): Promise<void>;
  /**
   * The master social opt-in. Accounts start private; this is the only way
   * to join (or leave) the social layer. Server-controlled.
   */
  setSocialEnabled(enabled: boolean, intents?: ConnectionIntent[]): Promise<void>;

  // --- commerce -------------------------------------------------------------
  getEntitlement(): Promise<EntitlementSnapshot>;
  /**
   * Verified purchase. `platform` defaults to the real flow; 'dev' requests a
   * developer unlock, which the server grants only for allowlisted accounts.
   */
  purchase(productId: string, platform?: 'play' | 'mock' | 'dev'): Promise<{ granted: string }>;
  listLedger(): Promise<CreditLedgerEntry[]>;

  // --- Astrally AI ----------------------------------------------------------
  allyTurn(request: AllyTurnRequest): Promise<AllyTurnResponse>;

  listMemories(): Promise<MemoryItem[]>;
  addMemory(item: MemoryItem): Promise<void>;
  resolveMemory(
    memoryId: string,
    action: 'confirm' | 'reject' | 'delete' | 'edit',
    content?: string,
    cloudApproved?: boolean,
  ): Promise<void>;

  // --- Ally-surfaced recommendations (Home "people aligned with you") -------
  generateRecommendations(): Promise<number>;
  listRecommendations(): Promise<RecommendationDoc[]>;
  setRecommendationStatus(
    recId: string,
    status: 'seen' | 'dismissed' | 'not-similar',
  ): Promise<void>;

  // --- discovery + connections ----------------------------------------------
  discoveryFeed(): Promise<DiscoveryFeedResult>;
  discoveryAction(targetUid: string, action: 'pass' | 'save' | 'unsave'): Promise<void>;
  compatibilityGet(targetUid: string): Promise<CompatibilityView>;
  connectionRequest(
    recipientUid: string,
    introMessage?: string,
  ): Promise<{ connectionId: string; quota: { remaining: number; kind: string } }>;
  connectionRespond(
    connectionId: string,
    accept: boolean,
  ): Promise<{ status: string; conversationId?: string }>;
  connectionWithdraw(connectionId: string): Promise<void>;
  listConnections(): Promise<{ incoming: ConnectionDoc[]; outgoing: ConnectionDoc[] }>;

  // --- human messaging ------------------------------------------------------
  listConversations(): Promise<ConversationDoc[]>;
  listMessages(conversationId: string): Promise<MessageDoc[]>;
  /**
   * Rejects when the message is refused. Automated moderation runs BEFORE the
   * message is stored, so a refusal means nothing was written and the caller
   * still owns the text — keep it in the composer. Use moderationRefusal()
   * above to tell a moderation refusal from an ordinary failure, and
   * mediaProRequired() for the tier refusal on an attachment.
   *
   * `text` may be empty ONLY when `media` is present — a photo with no caption
   * is a normal message.
   *
   * `media` must come from uploadPostMedia() (which is the shared media
   * pipeline, not a feed-only one): a MediaRef built by hand is rejected,
   * because the server requires the moderation receipt for those exact bytes.
   *
   * The result is what tells the sender whether the person on the other side
   * can actually open the attachment. Implementations that predate media
   * resolve with nothing, so callers must treat a void result as "no answer",
   * never as "yes they can".
   */
  sendMessage(
    conversationId: string,
    text: string,
    replyTo?: string,
    media?: MediaRef,
  ): Promise<SentMessage | void>;
  reactToMessage(conversationId: string, messageId: string, emoji: string | null): Promise<void>;
  editMessage(conversationId: string, messageId: string, text: string): Promise<void>;
  deleteMessageForSelf(conversationId: string, messageId: string): Promise<void>;
  endConversation(conversationId: string): Promise<void>;

  // --- the members-only feed ------------------------------------------------
  /**
   * One page, newest first. Never unbounded: the server clamps `limit` and
   * returns a cursor. Rejects for accounts that have not joined the social
   * layer — callers should not offer the feed to a private account at all.
   */
  listFeed(opts?: { cursor?: string; limit?: number }): Promise<FeedPage>;
  /**
   * Publish a post. Moderation runs BEFORE anything is stored, so a refusal
   * means nothing was written and the caller still owns the text — keep it in
   * the composer. Use moderationRefusal() above to tell a moderation refusal
   * from an ordinary failure, and mediaProRequired() for the tier refusal.
   *
   * `media` must come from uploadPostMedia(); a MediaRef built by hand will be
   * rejected, because the server requires the moderation receipt for those
   * exact bytes.
   */
  createPost(text: string, media?: MediaRef): Promise<FeedPost>;
  /** Your own post only. Removes the stored file with it. */
  deletePost(postId: string): Promise<void>;
  reportPost(postId: string, reason: ReportReason, context?: string): Promise<void>;

  // --- feed engagement --------------------------------------------------------
  /**
   * Like (`on: true`) or unlike (`on: false`) a post. IDEMPOTENT IN BOTH
   * DIRECTIONS — liking twice is one like, unliking what was never liked is a
   * no-op — so a double tap or a retry after a dropped connection is safe.
   *
   * Returns the server's own count. Callers rendering optimistically should
   * reconcile to this value rather than keep their guess: someone else may
   * have liked the same post in the meantime.
   */
  toggleLike(postId: string, on: boolean): Promise<FeedLikeResult>;
  /**
   * Publish a comment (at most 500 characters). Moderated BEFORE it is stored,
   * exactly like a post or a message, so a refusal means nothing was written
   * and the caller still owns the text — keep it in the composer and use
   * moderationRefusal() to tell a refusal from an ordinary failure.
   *
   * RETURNS THE RECEIPT, NOT THE COMMENT — the id the server assigned and what
   * moderation actually did. That is the whole of the callable's contract, and
   * an adapter that padded it out into a full FeedComment would be inventing
   * `authorName` and `createdAtISO` rather than reporting them. The caller
   * knows its own display name and can render the row from these two fields
   * plus what it already has; see CommentsSheet.
   */
  addComment(postId: string, text: string): Promise<FeedCommentReceipt>;
  /** Your own comment, or any comment on your own post. Decrements the count. */
  deleteComment(postId: string, commentId: string): Promise<void>;
  /** One page of comments, newest first, 20 at a time. */
  listComments(postId: string, cursor?: string): Promise<FeedCommentsPage>;
  /**
   * Grant → upload → moderate, in that order, returning the MediaRef to
   * attach. Pro only; rejects with mediaProRequired() otherwise. Images are
   * checked by a provider before this resolves; VIDEO IS NOT CHECKED BY
   * ANYTHING, and the composer says so before a file is picked.
   *
   * NOT FEED-ONLY despite the name — ConversationView uses it for chat
   * attachments too, because the pipeline is identical (the upload grant does
   * not know or care which surface the file is destined for) and a second copy
   * of it would be a second place to forget the Cache-Control. The name is a
   * leftover from the feed landing first; `uploadMedia` when someone is next
   * in this file.
   */
  uploadPostMedia(prepared: PreparedUpload): Promise<MediaRef>;
  /**
   * Fetch the real bytes behind a MediaRef path. Pro only — the refusal comes
   * from storage.rules, not from here.
   *
   * BY PATH, NEVER BY DOWNLOAD URL. A download URL carries a token that
   * bypasses Storage rules for anyone holding the link, so putting one on a
   * document non-Pro clients read would hand out exactly what the tier check
   * withholds. Non-Pro viewers never call this at all — they render the
   * blurhash the post already carries.
   */
  loadMedia(path: string): Promise<Blob>;

  // --- safety ---------------------------------------------------------------
  blockUser(targetUid: string): Promise<void>;
  reportUser(targetUid: string, reason: ReportReason, context?: string): Promise<void>;

  deleteAccount(): Promise<void>;
  exportData(): Promise<Record<string, unknown>>;

  /** Demo-only: switch between seeded demo accounts. */
  switchDemoUser?(uid: string): Promise<void>;
  listDemoUsers?(): Promise<UserDoc[]>;
}
